import React, { useEffect, useState } from 'react';
import styles from './investment-target.module.css';

import {
  BackNextSkip,
  clearValidation,
  isValidAmount,
  isValidTargetAmount,
  isValidYear,
  required,
  ruleRunner,
  run,
  TextInput,
} from '@assetmark/common-elements/';
import { TGoal } from '../../models/goal';
import { currencyFormatter } from '@assetmark/utilities';

import { TGoalType } from '../../models/goal-type.model';
import { convertDollarStringToNumber } from '../../utils/format.utils';

export interface TInvestmentTargetProps {
  history?: any;
  goal?: TGoal;
  goalTypes?: TGoalType[];
  investmentAmount: number;
  onBackButtonClick?: () => void;
  onSkipGoal?: () => void;
  saveInvestmentTarget: (
    targetYear: number,
    annualAmount: number,
    targetAmount: any,
  ) => void;
}

const InvestmentTarget: React.FC<TInvestmentTargetProps> = (props) => {
  const currentYear = new Date().getFullYear();
  const [goal, setGoal] = useState<TGoal>(props.goal);
  const [goalTypes, setGoalTypes] = useState<TGoalType[]>(
    props.goalTypes || [],
  );
  const [buttonClass, setButtonClass] = useState<string>('disabled');
  const [annualAmt, setAnnualAmt] = useState<string>(
    props.goal.AnnualSavings
      ? currencyFormatter(props.goal.AnnualSavings / 12)
      : currencyFormatter(props.goal.AnnualSavings),
  );
  const [targetAmt, setTargetAmt] = useState<string>(
    String(props.goal.TargetAmount !== 0 ? props.goal.TargetAmount : ''),
  );
  const [investmentAmount, setInvestmentAmount] = useState<number>(
    props.investmentAmount,
  );
  const [showErrors, setShowErrors] = useState<boolean>(false);
  const [validationErrors, setValidationErrors] = useState({
    TargetAmount: '',
    AnnualSavings: '',
    TargetYear: '',
  });
  const [fieldValidations, setFieldValidations] = useState<{
    TargetAmount: (state: any) => void;
    AnnualSavings: (state: any) => void;
    TargetYear: (state: any) => void;
  }>({
    TargetYear: ruleRunner('TargetYear', 'year', [
      required,
      isValidYear(currentYear + 1, currentYear + 120),
    ]),
    AnnualSavings: ruleRunner('AnnualSavings', 'amount', [
      required,
      isValidAmount(0, 999999),
    ]),
    TargetAmount: (state: any) => {},
  });
  const [isGoalProfileDirty, setIsGoalProfileDirty] = useState<boolean>(false);
  const [isComplete, setIsComplete] = useState<boolean>(false);

  useEffect(() => {
    if (props.goal && props.goal.Amount) {
      setInvestmentAmount(Number(props.goal.Amount));
    }

    if (props.goal && props.goal.TargetAmount) {
      setTargetAmt(currencyFormatter(props.goal.TargetAmount));
    }

    if (props.goal && props.goal.AnnualSavings) {
      const annAmt = props.goal.AnnualSavings
        ? currencyFormatter(props.goal.AnnualSavings / 12)
        : currencyFormatter(props.goal.AnnualSavings);
      setAnnualAmt(annAmt);
    }
    setIsComplete(isInvestmentTargetComplete(props.goal));
  }, [props.goal.Amount, props.goal.AnnualSavings, props.goal.TargetAmount]);

  useEffect(() => {
    setButtonClass(isComplete ? '' : 'disabled');
  }, [isComplete]);

  const handleBackButtonClick = (event: any): void => {
    props.onBackButtonClick && props.onBackButtonClick();
  };
  const handleSaveGoal = (event: any): void => {
    console.log(
      goal.TargetYear,
      convertDollarStringToNumber(annualAmt),
      targetAmt,
    );
    props.saveInvestmentTarget(
      goal.TargetYear,
      convertDollarStringToNumber(annualAmt),
      targetAmt,
    );
  };
  const handleSkipGoal = (event: any): void => {
    props.onSkipGoal && props.onSkipGoal();
  };
  const handleOnChange = (event: any): void => {
    const field = event.target.name;
    const amount = convertDollarStringToNumber(event.target.value);
    const amountFormated = amount
      ? currencyFormatter(event.target.value)
      : event.target.value;
    const updatedGoal = { ...goal };
    if (field === 'TargetAmount') {
      setTargetAmt(amountFormated);
      updatedGoal[field] = amount;
    } else {
      setAnnualAmt(amountFormated);
      updatedGoal[field] = amount * 12;
    }

    setGoal(updatedGoal);
    setIsGoalProfileDirty(true);
    validateProfile(field, true);
  };

  const validateProfile = (field: string, onChange: boolean): void => {
    const currentGoalState = { ...goal };
    const currentFieldValidations = {
      ...fieldValidations,
      TargetAmount: ruleRunner('TargetAmount', 'amount', [
        required,
        isValidTargetAmount(10000, 999999999, investmentAmount),
      ]),
    };
    const errors = onChange
      ? run(currentGoalState, [ruleRunner(field, '', [clearValidation])])
      : run(currentGoalState, [currentFieldValidations[field]]);
    const updatedValidationErrors = { ...validationErrors, ...errors };
    setIsComplete(
      isInvestmentTargetComplete(currentGoalState, updatedValidationErrors),
    );
    setShowErrors(true);
    setValidationErrors(updatedValidationErrors);
  };

  const isInvestmentTargetComplete = (
    goal: TGoal,
    validationErrors?: any,
  ): boolean => {
    let returnItem = false;
    let errorOnPage = false;

    if (
      validationErrors &&
      ((validationErrors.TargetAmount &&
        validationErrors.TargetAmount.length > 0) ||
        (validationErrors.AnnualSavings &&
          validationErrors.AnnualSavings.length > 0) ||
        (validationErrors.TargetYear && validationErrors.TargetYear.length > 0))
    ) {
      errorOnPage = true;
    }
    if (
      investmentAmount &&
      goal.TargetAmount > investmentAmount &&
      goal.TargetAmount <= 999999999 &&
      goal.TargetYear > 0 &&
      goal.AnnualSavings >= 0 &&
      errorOnPage === false &&
      String(goal.AnnualSavings) !== ''
    ) {
      returnItem = true;
    }
    return returnItem;
  };

  const updateGoalState = (event: any): void => {
    const field = event.target.name;
    const updatedGoal = { ...goal };
    updatedGoal[field] = event.target.value;
    setGoal(updatedGoal);
    setIsGoalProfileDirty(true);
    validateProfile(field, true);
  };

  return (
    <div className={styles.investment_target_component}>
      <div className="col-sm-12">
        <h1 className={`${styles.title}`}>Investment Target</h1>
        <div className={`row mt-4 ${styles.label_text}`}>
          <div className="col-sm-12">
            <div className="row ">
              <div className={`row ${styles.mb25}`}>
                <div className={`col-sm-12 ${styles.formText}`}>
                  I want my ${currencyFormatter(investmentAmount)} investment to
                  grow to
                </div>
              </div>
            </div>
            <div className={`row ${styles.mb25}`}>
              <div className={`col-sm-12 ${styles.formText}`}>
                ${' '}
                <TextInput
                  className={`${styles.line_amount} ${
                    validationErrors.TargetAmount &&
                    validationErrors.TargetAmount.length
                      ? styles.errorClass
                      : ''
                  }`}
                  value={targetAmt}
                  placeHolder=""
                  showError={showErrors}
                  errorText={validationErrors.TargetAmount}
                  onChange={handleOnChange}
                  onBlur={(e) => validateProfile(e.target.name, false)}
                  name="TargetAmount"
                  type="underline"
                  maxLength={11}
                />{' '}
                by
                <TextInput
                  className={`${styles.line_year} ${
                    validationErrors.TargetYear &&
                    validationErrors.TargetYear.length
                      ? styles.errorClass
                      : ''
                  }`}
                  value={goal && goal.TargetYear}
                  placeHolder="YYYY"
                  showError={showErrors}
                  errorText={validationErrors.TargetYear}
                  onChange={updateGoalState}
                  onBlur={(e) => validateProfile(e.target.name, false)}
                  name="TargetYear"
                  type="underline"
                  maxLength={4}
                />
              </div>
            </div>
            <div className="row">
              <div className={`col-sm-12 ${styles.formText}`}>
                and I am contributing ${' '}
                <TextInput
                  className={`${styles.line_contribution} ${
                    validationErrors.AnnualSavings &&
                    validationErrors.AnnualSavings.length
                      ? styles.errorClass
                      : ''
                  }`}
                  value={annualAmt}
                  placeHolder=""
                  showError={showErrors}
                  errorText={validationErrors.AnnualSavings}
                  onChange={handleOnChange}
                  onBlur={(e) => validateProfile(e.target.name, false)}
                  name="AnnualSavings"
                  type="underline"
                  maxLength={7}
                />{' '}
                per month
              </div>
            </div>
            <div className={styles.mt_row_btn}>
              <BackNextSkip
                backButtonClassName={`button button-secondary button-lg ${styles.btnLineHeight}`}
                backButtonText="BACK"
                onBackButtonClickHandler={handleBackButtonClick}
                nextButtonClassName={`button button-primary button-lg ml-4 mr-2 ${styles.btnLineHeight} ${buttonClass}`}
                nextButtonText="ASSESS RISK PROFILE"
                onNextButtonClickHandler={handleSaveGoal}
                skipText="Skip This Step"
                onSkipClickHandler={handleSkipGoal}
                isDisabledNextButton={
                  goal.TargetYear === 0 ||
                  String(goal.TargetYear) === '' ||
                  targetAmt === '' ||
                  annualAmt === '' ||
                  buttonClass
                    ? true
                    : false
                }
              />
            </div>
          </div>
        </div>
      </div>
      <div
        className={`col-sm-4 mt-5 p-0 ${styles.investment_return_tooltip}`}
      ></div>
    </div>
  );
};

export default InvestmentTarget;
